import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { FaLinkedin, FaGithub, FaTwitter, FaPencilAlt, FaMapMarkerAlt, FaGlobe, FaPhone, FaCog } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import EditProfileModal from "@/components/modals/EditProfileModal";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@apollo/client";
import { GET_PROFILE } from "@/lib/graphql/queries";
import { getProfilePicture, Profile as ProfileType, updateCoverPhoto, updateProfile as updateProfileService, updateProfilePhoto } from "@/services/profileService";
import { getCookie } from "@/utils/cookies";
import ImageUpload from "@/components/ui/image-upload";
import { useDispatch } from "react-redux";
import { updateUserPhoto } from "@/features/auth/authSlice";

// Custom hook to detect if the screen size is mobile
const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

// Card component for consistent section styling
const Card = ({ title, children }) => (
  <div className="bg-white p-4 rounded-lg shadow-md">
    <h2 className="text-xl font-bold mb-2">{title}</h2>
    {children}
  </div>
);

// Main Profile component
const Profile = () => {
  const dispatch = useDispatch();

  // State declarations
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [profileImage, setProfileImage] = useState<string>("/default-avatar.png");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    id: "",
    username: "",
    title: "",
    location: "",
    bio: "",
    email: "",
    skills: [] as string[],
    roles: [] as string[],
    linkedin: "",
    github: "",
    x: "",
    website: "",
    mobile: "",
    experiences: [] as any[],
    certifications: [] as any[],
    education: [] as any[]
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Custom hooks
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { toast } = useToast();

  // GraphQL query
  const { data: profileQueryData, loading, error } = useQuery<{ getProfile: ProfileType }>(GET_PROFILE, {
    context: {
      headers: {
        Authorization: `Bearer ${getCookie("token")}`,
      },
    },
  });

  // Effect to populate profile data and load images
  useEffect(() => {
    if (profileQueryData?.getProfile) {
      // Set all fields from getProfile to ensure all data is displayed
      setProfileData({
        id: profileQueryData.getProfile.id || "",
        username: profileQueryData.getProfile.username || "",
        title: profileQueryData.getProfile.title || "",
        location: profileQueryData.getProfile.location || "",
        bio: profileQueryData.getProfile.bio || "",
        email: profileQueryData.getProfile.email || "",
        skills: profileQueryData.getProfile.skills || [],
        roles: profileQueryData.getProfile.roles || [],
        linkedin: profileQueryData.getProfile.linkedin || "",
        github: profileQueryData.getProfile.github || "",
        x: profileQueryData.getProfile.x || "",
        website: profileQueryData.getProfile.website || "",
        mobile: profileQueryData.getProfile.mobile || "",
        experiences: profileQueryData.getProfile.experiences || [],
        certifications: profileQueryData.getProfile.certifications || [],
        education: profileQueryData.getProfile.education || []
      });

      // Load profile photo
      if (profileQueryData.getProfile.profilePhoto) {
        (async () => {
          try {
            const result = await getProfilePicture(profileQueryData.getProfile.profilePhoto);
            const imageUrl = `data:${result.mimetype};base64,${result.base64Data}`;
            setProfileImage(imageUrl);
          } catch (error) {
            console.error("Error loading profile picture:", error);
          }
        })();
      }

      // Load cover photo
      if (profileQueryData.getProfile.coverPhoto) {
        (async () => {
          try {
            const result = await getProfilePicture(profileQueryData.getProfile.coverPhoto);
            const imageUrl = `data:${result.mimetype};base64,${result.base64Data}`;
            setCoverImage(imageUrl);
          } catch (error) {
            console.error("Error loading cover photo:", error);
          }
        })();
      }
    }
  }, [profileQueryData]);



  // Handlers
  const handleProfilePhotoChange = useCallback(
    async (file: File) => {
      setIsUpdating(true);
      try {
        // Show preview immediately
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === "string") {
            setProfileImage(reader.result);
          }
        };
        reader.readAsDataURL(file);

        // Upload to server
        const updatedProfile = await updateProfilePhoto(file);

        // Update Redux store
        if (updatedProfile.profilePhoto) {
          dispatch(updateUserPhoto(updatedProfile.profilePhoto));
        }

        toast({
          title: "Success",
          description: "Profile photo updated successfully",
        });
      } catch (error) {
        console.error("Error updating profile photo:", error);
        toast({
          title: "Error",
          description: "Failed to update profile photo",
          variant: "destructive",
        });
        // Revert to previous image on failure
        if (profileQueryData?.getProfile?.profilePhoto) {
          const result = await getProfilePicture(profileQueryData.getProfile.profilePhoto);
          const imageUrl = `data:${result.mimetype};base64,${result.base64Data}`;
          setProfileImage(imageUrl);
        }
      } finally {
        setIsUpdating(false);
      }
    },
    [dispatch, toast, profileQueryData]
  );

  const handleCoverPhotoChange = useCallback(
    async (file: File) => {
      setIsUpdating(true);
      try {
        // Show preview immediately
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === "string") {
            setCoverImage(reader.result);
          }
        };
        reader.readAsDataURL(file);

        // Upload to server
        await updateCoverPhoto(file);

        toast({
          title: "Success",
          description: "Cover photo updated successfully",
        });
      } catch (error) {
        console.error("Error updating cover photo:", error);
        toast({
          title: "Error",
          description: "Failed to update cover photo",
          variant: "destructive",
        });
        // Revert to previous image on failure (if applicable)
        if (profileQueryData?.getProfile?.coverPhoto) {
          const result = await getProfilePicture(profileQueryData.getProfile.coverPhoto);
          const imageUrl = `data:${result.mimetype};base64,${result.base64Data}`;
          setCoverImage(imageUrl);
        }
      } finally {
        setIsUpdating(false);
      }
    },
    [toast, profileQueryData]
  );

  const handleProfileUpdate = useCallback(
    async (updatedData: Partial<typeof profileData>) => {
      setIsUpdating(true);
      try {
        const updatedProfile = await updateProfileService(updatedData);
        setProfileData((prev) => ({
          ...prev,
          ...updatedProfile,
        }));
        toast({
          title: "Success",
          description: "Profile updated successfully",
        });
        setIsEditModalOpen(false);
      } catch (error) {
        console.error("Error updating profile:", error);
        toast({
          title: "Error",
          description: "Failed to update profile",
          variant: "destructive",
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [toast]
  );

  // Loading and error states
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-destructive">Error loading profile</h2>
          <p className="text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  // Render
  return (
    <div className="min-h-screen bg-muted/30">
  {/* Cover Photo Section */}
  <div className="relative">
    <ImageUpload 
      currentImage={coverImage} 
      onImageChange={handleCoverPhotoChange} 
      className="h-[200px] sm:h-[300px] w-full object-cover"
    />
  </div>

  {/* Profile Header */}
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-[100px] sm:-mt-[120px]">
    <div className="relative bg-background rounded-lg shadow-lg p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row gap-6">
        <div className="relative mx-auto sm:mx-0 -mt-20 sm:-mt-24">
          <ImageUpload 
            currentImage={profileImage} 
            onImageChange={handleProfilePhotoChange} 
            isProfile 
            className="w-32 h-32 sm:w-36 sm:h-36 rounded-full border-4 border-background"
          />
        </div>

        <div className="flex-1 mt-4 sm:mt-0 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">{profileData.username}</h1>
              <p className="text-lg text-muted-foreground mt-1">{profileData.title}</p>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 mt-3 text-sm text-muted-foreground">
                {profileData.location && (
                  <span className="flex items-center">
                    <FaMapMarkerAlt className="mr-1 h-4 w-4" />
                    {profileData.location}
                  </span>
                )}
                {profileData.mobile && (
                  <span className="flex items-center">
                    <FaPhone className="mr-1 h-4 w-4" />
                    {profileData.mobile}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                className="flex items-center gap-2 w-full sm:w-auto"
                onClick={() => setIsEditModalOpen(true)}
              >
                <FaPencilAlt className="w-4 h-4" />
                Edit Profile
              </Button>
              <Button
                variant="secondary"
                className="flex items-center gap-2 w-full sm:w-auto"
                onClick={() => navigate('/settings')}
              >
                <FaCog className="w-4 h-4" />
                Settings
              </Button>
            </div>
          </div>

          <div className="flex gap-4 mt-6 justify-center sm:justify-start">
            {profileData.linkedin && (
              <a
                href={profileData.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition"
              >
                <FaLinkedin size={24} />
              </a>
            )}
            {profileData.github && (
              <a
                href={`https://github.com/${profileData.github}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition"
              >
                <FaGithub size={24} />
              </a>
            )}
            {profileData.x && (
              <a
                href={`https://x.com/${profileData.x}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition"
              >
                <FaTwitter size={24} />
              </a>
            )}
            {profileData.website && (
              <a
                href={profileData.website.startsWith("http") ? profileData.website : `https://${profileData.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition"
              >
                <FaGlobe size={24} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Main Content */}
    <div className="mt-8 grid grid-cols-1 gap-6">
      {/* Skills Section - Mobile */}
      <div className="block md:hidden">
        <Card title="Skills">
          <div className="flex flex-wrap gap-2">
            {profileData.skills?.map((skill, index) => (
              <Badge key={index} variant="secondary">
                {skill}
              </Badge>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Card title="About">
            <p className="text-muted-foreground whitespace-pre-wrap">{profileData.bio || 'No bio available.'}</p>
          </Card>

          <Card title="Experience">
            {profileData.experiences?.length > 0 ? (
              profileData.experiences.map((exp, index) => (
                <div key={index} className="mb-6 last:mb-0">
                  <h4 className="font-semibold text-lg">{exp.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {exp.company} · {exp.duration}
                  </p>
                  <p className="mt-2 text-sm">{exp.description}</p>
                  {exp.accomplishments?.length > 0 && (
                    <div className="mt-3">
                      <p className="font-medium text-sm">Accomplishments:</p>
                      <ul className="list-disc pl-5 mt-1 text-sm">
                        {exp.accomplishments.map((acc, i) => (
                          <li key={i}>{acc}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {index < profileData.experiences.length - 1 && <Separator className="my-6" />}
                </div>
              ))
            ) : (
              <p className="text-muted-foreground">No experience information available.</p>
            )}
          </Card>

          {profileData.education?.length > 0 && (
            <Card title="Education">
              {profileData.education.map((edu, index) => (
                <div key={index} className="mb-6 last:mb-0">
                  <h4 className="font-semibold text-lg">{edu.degree} in {edu.fieldOfStudy}</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {edu.institution} · {edu.startDate} - {edu.endDate}
                  </p>
                  {edu.description && <p className="mt-2 text-sm">{edu.description}</p>}
                  {index < profileData.education.length - 1 && <Separator className="my-6" />}
                </div>
              ))}
            </Card>
          )}
        </div>

        <div className="hidden md:block space-y-6">
          <Card title="Skills">
            <div className="flex flex-wrap gap-2">
              {profileData.skills?.map((skill, index) => (
                <Badge key={index} variant="secondary">
                  {skill}
                </Badge>
              ))}
            </div>
          </Card>

          <Card title="Certifications">
            {profileData.certifications?.length > 0 ? (
              profileData.certifications.map((cert, index) => (
                <div key={index} className="flex items-start gap-4 mb-4 last:mb-0">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.11"></path>
                      <path d="M15 7a3 3 0 1 0-6 0v0h6v0z"></path>
                      <path d="M13.83 10H7.5a2.5 2.5 0 1 1 0-5h2"></path>
                      <path d="M16.5 10h2a2.5 2.5 0 0 0 0-5H13"></path>
                    </svg>
                  </div>
                  <div>
                    <span className="text-sm font-medium">{cert.name}</span>
                    {cert.url && (
                      <a 
                        href={cert.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="block text-xs text-primary hover:underline mt-1"
                      >
                        View Certificate
                      </a>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground">No certifications available.</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  </div>

  {isMobile && (
    <button
      onClick={() => setIsEditModalOpen(true)}
      className="fixed bottom-6 right-6 bg-primary text-primary-foreground rounded-full p-4 shadow-lg hover:bg-primary/90 transition-colors z-50"
    >
      <FaPencilAlt className="w-5 h-5" />
    </button>
  )}

  <EditProfileModal
    isOpen={isEditModalOpen}
    onClose={() => setIsEditModalOpen(false)}
    profileData={profileData}
    onSave={handleProfileUpdate}
    isLoading={isUpdating}
  />
</div>
  );
};

export default Profile;